<template>
  <div>
    <div class="d-flex align-center mb-3">
      <h3 class="input-text-size">Export Queue</h3>
      <v-spacer />
      <v-switch
        v-model="autoRefresh"
        label="Auto-refresh"
        dense
        class="mt-0 input-text-size"
        hide-details
      />
    </div>
    <div class="pb-2">
      <v-data-table
        :headers="headers"
        :items="fileQueue"
        :loading="loading"
        :sort-by="['timestamp']"
        :sort-desc="[true]"
        dense
        class="elevation-1"
      >
        <template #[`item.timestamp`]="{ item }">
          {{ formatDate(item.timestamp) }}
        </template>
        <template #[`item.Status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.Status)"
            small
            dark
          >
            {{ item.Status }}
          </v-chip>
        </template>
        <template #[`item.progress`]="{ item }">
          <div v-if="item.Status === 'Processing' || item.Status === 'Completed'">
            <v-progress-linear
              :value="getProgress(item)"
              :color="item.Status === 'Completed' ? 'success' : 'primary'"
              height="20"
              rounded
            >
              <template #default>
                <span class="white--text text-caption">
                  {{ item.Records }} / {{ item.TotalRecords }}
                </span>
              </template>
            </v-progress-linear>
          </div>
          <div v-else-if="item.Status === 'Failed' || item.Status === 'Error'">
            <span class="error--text">{{ item.Errors }} error(s)</span>
          </div>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="item.Status === 'Completed'"
            icon
            x-small
            color="primary"
            title="Download"
            @click="downloadFile(item)"
          >
            <v-icon small>
              {{ mdiDownload }}
            </v-icon>
          </v-btn>
          <v-btn
            v-if="item.Status === 'Failed' || item.Status === 'Error'"
            icon
            x-small
            color="info"
            title="View Errors"
            @click="viewErrors(item)"
          >
            <v-icon small>
              {{ mdiAlertCircle }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            x-small
            color="error"
            title="Delete"
            :disabled="item.Status === 'Processing'"
            @click="confirmDelete(item)"
          >
            <v-icon small>
              {{ mdiDelete }}
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- Error Details Dialog -->
    <v-dialog
      v-model="errorDialog"
      max-width="800"
    >
      <v-card>
        <v-card-title class="py-2 text-h6">
          Error Details
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item
              v-for="(error, index) in selectedItemErrors"
              :key="index"
            >
              <v-list-item-content>
                <v-list-item-title>{{ error.ErrorDescription }}</v-list-item-title>
                <v-list-item-subtitle v-if="error.LineNumber">
                  Line: {{ error.LineNumber }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            small
            @click="errorDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="py-2 text-h6">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this export job?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            small
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            small
            @click="deleteItem"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { mdiDownload, mdiDelete, mdiAlertCircle } from '@mdi/js';
import { clients } from '../../../util/clients';

export default {
  name: 'FileQueueTable',
  data() {
    return {
      mdiDownload,
      mdiDelete,
      mdiAlertCircle,
      fileQueue: [],
      loading: false,
      autoRefresh: true,
      refreshInterval: null,
      errorDialog: false,
      deleteDialog: false,
      selectedItem: null,
      selectedItemErrors: [],
      headers: [
        { text: 'Date/Time', value: 'timestamp', width: '180px' },
        { text: 'File Name', value: 'FileName' },
        { text: 'Definition', value: 'FileDefinitionName', width: '200px' },
        { text: 'Status', value: 'Status', width: '120px' },
        {
          text: 'Progress', value: 'progress', width: '200px', sortable: false,
        },
        {
          text: 'Actions', value: 'actions', width: '120px', sortable: false,
        },
      ],
    };
  },
  watch: {
    autoRefresh(value) {
      if (value) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
  },
  async mounted() {
    await this.loadQueue();
    // Start auto-refresh if enabled
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    ...mapActions('fileQueue', [
      'loadFileQueues',
      'deleteFileQueue',
    ]),
    async loadQueue() {
      try {
        this.loading = true;
        const results = await this.loadFileQueues({
          criteria: {
            Program: 'MTGEXPORT',
          },
          fields: {
            _id: 1,
            timestamp: 1,
            FileName: 1,
            FileDefinitionName: 1,
            Status: 1,
            Records: 1,
            TotalRecords: 1,
            Errors: 1,
            ErrorDetail: {
              LineNumber: 1,
              ErrorDescription: 1,
            },
          },
        });
        this.fileQueue = results || [];
      } catch (error) {
        console.error('Error loading file queue:', error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleString();
    },
    getStatusColor(status) {
      const colors = {
        Pending: 'grey',
        Processing: 'blue',
        Completed: 'green',
        Failed: 'red',
        Error: 'orange',
      };
      return colors[status] || 'grey';
    },
    getProgress(item) {
      if (!item.TotalRecords || item.TotalRecords === 0) return 0;
      return Math.round((item.Records / item.TotalRecords) * 100);
    },
    async downloadFile(item) {
      try {
        // Use the backend REST base URL (same pattern as other downloads in the app)
        const { backendRest } = clients.direct;
        const url = `${backendRest.defaults.baseURL}/exports/download/${item._id}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = item.FileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },
    viewErrors(item) {
      this.selectedItem = item;
      this.selectedItemErrors = item.ErrorDetail || [];
      this.errorDialog = true;
    },
    confirmDelete(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },
    async deleteItem() {
      try {
        if (!this.selectedItem) return;
        await this.deleteFileQueue({
          _id: this.selectedItem._id,
        });
        this.deleteDialog = false;
        this.selectedItem = null;
        await this.loadQueue();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.loadQueue();
      }, 5000); // Refresh every 5 seconds
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
  },
};
</script>

<style scoped>
</style>
